import { useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix iconos de Leaflet con bundlers (Vite)
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';

// @ts-expect-error _getIconUrl es interno de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
});

const startIcon = L.divIcon({
  className: 'move-map-pin',
  html: '<div style="width:18px;height:18px;border-radius:50%;background:hsl(0 72% 44%);border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.25);"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

const endIcon = L.divIcon({
  className: 'move-map-pin',
  html: '<div style="width:18px;height:18px;border-radius:50%;background:hsl(18 94% 54%);border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.25);"></div>',
  iconSize: [18, 18],
  iconAnchor: [9, 9],
});

interface Geometry {
  type?: string;
  coordinates?: number[][]; // [lng, lat]
}

interface RouteMapProps {
  geometry?: Geometry | null;
  fallbackPoint?: { lat: number; lng: number } | null;
  height?: number | string;
  rounded?: boolean;
  interactive?: boolean;
}

function FitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView(points[0], 15);
    } else {
      map.fitBounds(L.latLngBounds(points.map(p => L.latLng(p[0], p[1]))).pad(0.15));
    }
  }, [map, points]);
  return null;
}

export default function RouteMap({
  geometry,
  fallbackPoint,
  height = 192,
  rounded = true,
  interactive = true,
}: RouteMapProps) {
  const wrapRef = useRef<HTMLDivElement>(null);

  // Convertimos GeoJSON [lng, lat] → Leaflet [lat, lng]
  const points = useMemo<[number, number][]>(() => {
    if (geometry?.coordinates && Array.isArray(geometry.coordinates) && geometry.coordinates.length) {
      return geometry.coordinates
        .filter(c => Array.isArray(c) && c.length >= 2)
        .map(c => [Number(c[1]), Number(c[0])] as [number, number]);
    }
    if (fallbackPoint) return [[fallbackPoint.lat, fallbackPoint.lng]];
    return [];
  }, [geometry, fallbackPoint]);

  const center: [number, number] = points[0] ?? [40.9489, -4.1182]; // Segovia por defecto

  const containerStyle: React.CSSProperties = {
    width: '100%',
    height,
    borderRadius: rounded ? 'var(--move-radius)' : 0,
    overflow: 'hidden',
    boxShadow: 'var(--move-shadow-card)',
    background: 'var(--move-muted)',
  };

  if (!points.length && !fallbackPoint) {
    return (
      <div ref={wrapRef} style={{ ...containerStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: 12, color: 'var(--move-muted-fg)', margin: 0 }}>
          Sin geometría disponible
        </p>
      </div>
    );
  }

  return (
    <div ref={wrapRef} style={containerStyle}>
      <MapContainer
        center={center}
        zoom={14}
        style={{ width: '100%', height: '100%' }}
        scrollWheelZoom={interactive}
        dragging={interactive}
        doubleClickZoom={interactive}
        touchZoom={interactive}
        zoomControl={interactive}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {points.length > 1 && (
          <Polyline
            positions={points}
            pathOptions={{ color: 'hsl(0 72% 44%)', weight: 5, opacity: 0.9, lineCap: 'round', lineJoin: 'round' }}
          />
        )}
        {points.length > 0 && <Marker position={points[0]} icon={startIcon} />}
        {points.length > 1 && <Marker position={points[points.length - 1]} icon={endIcon} />}
        <FitBounds points={points} />
      </MapContainer>
    </div>
  );
}
